import { logError } from '@nrdy-marketing-engine/error-handling';
import { dataLayerPush } from '@nrdy-marketing-engine/gtm/src/data-layer-actions';
import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Payment from '../api/payment/payment';
import signContracts from '../api/user/contracts/sign-contracts';
import { transformMeToClient } from '../api/user/create-account';
import { useCartContext } from '../cart-context';
import { CURRENCY_CODE_MAP } from '../constants/currency-code-map';
import { jurisdictionToCountryCode } from '../lib/country-code/jurisdiction-to-country-code';
import { trackCartEvent, trackCartFailure } from '../lib/track-service/track-cart';

export type PaymentType =
  | 'ApplePayCard'
  | 'CreditCard'
  | 'AndroidPayCard'
  | 'PayPalAccount'
  | 'VenmoAccount';

function usePayment() {
  const { user, refetchUserAndStore, loading: userLoading, students } = useUserContext();
  const client = transformMeToClient(user);
  const { cartState } = useCartContext();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<Payment>(null);
  const [paymentRequestable, setPaymentRequestable] = useState(false);
  const [paymentType, setPaymentType] = useState<PaymentType>(null);
  const [failed, setFailed] = useState(false);

  const cartItemData = useMemo(
    () => ({
      currencyCode: CURRENCY_CODE_MAP[jurisdictionToCountryCode(cartState.cartJurisdictionId)],
      id: cartState.catalogItem?.id,
      item_type: cartState.catalogItem?.item_type,
      links: cartState.catalogItem?.links,
      list_price: cartState.catalogItem?.list_price,
      name: cartState.catalogItem?.name,
      subject_id: cartState.catalogItem?.subject_id,
    }),
    [cartState.catalogItem, cartState.cartJurisdictionId]
  );
  const dataLayerData = useMemo(
    () => ({
      currencyCode: CURRENCY_CODE_MAP[jurisdictionToCountryCode(cartState.cartJurisdictionId)],
      ecommerce: {
        items: [
          {
            id: cartState.catalogItem?.id,
            price: cartState.catalogItem?.price_cents,
          },
        ],
      },
      email: client?.email,
      phoneNumber: cartState.phoneNumber,
      zipCode: cartState.zipCode,
    }),
    [cartState, client]
  );

  useEffect(() => {
    if (userLoading) return;
    if (payment && client) return;

    const newPayment = new Payment(client);

    newPayment
      .build()
      .then(() => {
        setPayment(newPayment);

        newPayment.client.on('paymentMethodRequestable', () => {
          setPaymentRequestable(true);
          setLoading(true);

          newPayment.client.requestPaymentMethod().then((payload) => {
            trackCartEvent(
              'dropin_info_load_complete',
              {
                clientUuid: client.uuid,
              },
              'Client has sucessfully filled payment info. Payment method established.'
            );
            setPaymentType(payload.type);
            setLoading(false);
          });
        });

        newPayment.client.on('noPaymentMethodRequestable', () => {
          trackCartFailure(
            'dropin_info_load_not_requestable',
            {
              clientUuid: client.uuid,
              error: new Error('payment method not requestable'),
            },
            'Payment method not requestable. Client has to fill information again.'
          );

          setPaymentRequestable(false);
          setLoading(false);
        });
      })
      .catch(async (err) => {
        // TODO: RE-enable this. Temporarily turning this off, as it is causing a strange sideeffect regarding paypal loading
        // on page refresh it renders fine
        // setFailed(true);
        // This error is being ignored because it seems to have no harm, and always happens with react
        // and other SPAs. The long term solution should be to build the UI ourselves.
        if (err.name === 'DropinError') {
          console.warn(err);
          return;
        }
        await trackCartFailure(
          'checkout_dropin_load_error',
          {
            clientUuid: client.uuid,
            error: err,
          },
          err?.message
        );
        logError(err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, user]);

  // inferring from paymentType setting above that the user has changed payment info and
  // it is requestable & not loading anymore
  useEffect(() => {
    if (!paymentType) return;

    trackCartEvent(
      'payment_info_change',
      {
        clientUuid: client.uuid,
      },
      paymentType
    );

    dataLayerPush('payment_info_change', {
      ecommerce: {
        items: [
          {
            id: cartState.catalogItem.id,
            name: cartState.catalogItem.name,
            price: cartState.catalogItem.price_cents,
            quantity: 1,
          },
        ],
        payment_type: paymentType,
      },
    });
  }, [paymentType, cartState.catalogItem]);

  const submitPayment = useCallback(async () => {
    setLoading(true);
    try {
      const contractIds = cartState?.contracts?.map((contract) => contract?.uuid);
      await signContracts(client?.uuid, contractIds);
      const result = await payment.submit(cartState, students);
      await trackCartEvent(
        'checkout_payment_success',
        {
          clientUuid: client.uuid,
          metadata: { cartItemData },
        },
        (cartState.catalogItem?.price_cents / 100).toFixed(2)
      );

      if (result) {
        dataLayerPush('purchase_complete', dataLayerData);

        refetchUserAndStore();

        return result;
      }

      throw new Error('Unexpected error, could not submit payment');
    } catch (err) {
      dataLayerPush('purchase_failure', dataLayerData);

      await trackCartFailure(
        'checkout_payment_failure',
        {
          clientUuid: client.uuid,
          metadata: { cartItemData, error: err, errorMessage: err.message },
        },
        err.message
      );

      setLoading(false);
      payment.client.clearSelectedPaymentMethod();

      throw err;
    }
  }, [cartItemData, cartState, client, dataLayerData, payment, refetchUserAndStore]);

  return { failed, loading, paymentRequestable, paymentType, submitPayment };
}

export default usePayment;
